import { getUrlIdForTracking } from './trackingUtils'
import { ANALYTICS_METADATA } from './constants'
import type { newTileTrackingConfig, TrackingConfig, TrackingData, TrackingLinkConfig } from '../types/homepageCategories'

export const getNewTileTrackingConfig = (
  trackingData: newTileTrackingConfig & {
    sectionHeader?: string
    row?: number | undefined
    section?: string
  },
) => {
  const { destinationUrl, mpvId, tileHeader, productKey, position, sectionHeader, subSection, row, section, pageZone, sourcePagePath } = trackingData
  const customZone = row ? `${section}_${sectionHeader}_Row${row}` : section
  const urlId = getUrlIdForTracking(destinationUrl)
  const ctaValue = urlId || tileHeader

  const body = {
    sourcePagePath,
    destinationUrl: destinationUrl || '',
    pageZone: pageZone || customZone,
    tileHeader: ctaValue,
    mpvId,
    productKey,
    subSection,
    position: position || 0,
  }
  return newTileTrackingOutput(body)
}

export const getTrackingConfig = (trackingData: TrackingData) => {
  const { ctaCopy, ctaUrl, mpvId, productKey, position, sectionHeader, subsection, row, section, pageZone, sourcePagePath } = trackingData
  const customZone = row ? `${section}_${sectionHeader}_Row${row}` : section
  const urlId = getUrlIdForTracking(ctaUrl)
  const ctaValue = urlId || ctaCopy

  const body = {
    sourcePagePath,
    destinationPagePath: ctaUrl || '',
    pageZone: pageZone || customZone,
    ctaValue,
    mpvId,
    productKey,
    subsection,
    position: position || 0,
  }
  return trackingOutput(body)
}

const trackingOutput = (ctaTrackingConfig: TrackingConfig): TrackingLinkConfig => {
  const { destinationPagePath, ctaValue, mpvId, productKey, subSection, sourcePagePath, position, pageZone } = ctaTrackingConfig
  const { experimentId, experimentKey, variationId, variationKey } = ctaTrackingConfig
  const trimmedCtaValue = ctaValue?.trim()
  const parsedDestinationPagePath = removeQueryParamsFrom(destinationPagePath)
  const composedCtaValue = subSection ? `${subSection}:${trimmedCtaValue}` : trimmedCtaValue
  const eventDetail = `${sourcePagePath};${parsedDestinationPagePath};${pageZone};${composedCtaValue}`
  const navigationDetail = `${composedCtaValue}:${position}`

  const eventName = 'Navigation Clicked'
  const experimentProperties =
    experimentId && variationId
      ? {
          experimentId: experimentId.toString(),
          experimentName: experimentKey,
          variationId: variationId.toString(),
          variationName: variationKey,
        }
      : {}

  return {
    eventName,
    properties: {
      category: ANALYTICS_METADATA.pageName,
      label: `Content:${pageZone}`,
      eventDetail,
      navigationDetail: pageZone === 'Hero' ? trimmedCtaValue : navigationDetail,
      product_id: mpvId,
      core_product_id: productKey,
      ...experimentProperties,
    },
    linkAttributes: {
      'data-section': `Content:${pageZone}`,
      'data-position': position,
      'data-translation': composedCtaValue,
    },
  }
}

const newTileTrackingOutput = (ctaTrackingConfig: newTileTrackingConfig): TrackingLinkConfig => {
  const { destinationUrl, tileHeader, mpvId, productKey, subSection, sourcePagePath, position, pageZone } = ctaTrackingConfig
  const { experimentId, experimentKey, variationId, variationKey } = ctaTrackingConfig
  const trimmedTileHeader = tileHeader.trim()
  const parsedDestinationPagePath = removeQueryParamsFrom(destinationUrl)
  const composedCtaValue = subSection ? `${subSection}:${trimmedTileHeader}` : trimmedTileHeader
  const eventDetail = `${sourcePagePath};${parsedDestinationPagePath};${pageZone};${composedCtaValue}`
  const navigationDetail = `${composedCtaValue}:${position}`

  const eventName = 'Navigation Clicked'
  const experimentProperties =
    experimentId && variationId
      ? {
          experimentId: experimentId.toString(),
          experimentName: experimentKey,
          variationId: variationId.toString(),
          variationName: variationKey,
        }
      : {}

  return {
    eventName,
    properties: {
      category: 'Home Page',
      label: `Content:${pageZone}`,
      eventDetail,
      navigationDetail: pageZone === 'Hero' ? trimmedTileHeader : navigationDetail,
      product_id: mpvId,
      core_product_id: productKey,
      ...experimentProperties,
    },
    linkAttributes: {
      'data-section': `Content:${pageZone}`,
      'data-position': position,
      'data-translation': composedCtaValue,
    },
  }
}

export const getSeoZoneCtaTrackingConfig = (sourcePagePath: string, destinationPagePath: string, ctaValue: string, position: number): TrackingLinkConfig => {
  const pageZone = 'SEO Zone'
  const subSection = undefined

  return trackingOutput({ sourcePagePath, destinationPagePath, pageZone, ctaValue, subSection, position })
}

export const getTrackingConfigLinkAttributes = (trackingData: TrackingData) => {
  return getTrackingConfig(trackingData).linkAttributes || {}
}

export const removeQueryParamsFrom = (pagePath: string): string => {
  if (typeof window !== 'undefined') {
    return new URL(pagePath, window.location.origin).pathname
  }

  return pagePath
}
